import { FC, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';

import Button from 'components/_core/Button';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import Row from 'components/Row';

import locales from './locales';
import { Section } from './types';

interface SectionListProps {
  sections: Section[];
  fetchMore: () => Promise<unknown>;
  hasMore?: boolean;
}

const SectionList: FC<SectionListProps> = ({
  fetchMore,
  hasMore = false,
  sections,
}) => {
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const handleFetchMore = async () => {
    setIsFetchingMore(true);

    try {
      await fetchMore();
    } finally {
      setIsFetchingMore(false);
    }
  };

  return (
    <>
      {sections.map(({ data, key, title }) => (
        <section key={key}>
          <Text>{title}</Text>
          <SunshineCard.Group isBoxed={false} marginBottom="space-32">
            {data.map(
              ({
                description,
                downloadUrl,
                icon,
                key: sectionItemKey,
                title: sectionItemTitle,
              }) => (
                <SunshineCard
                  key={sectionItemKey}
                  {...(downloadUrl && {
                    as: 'a',
                    href: downloadUrl,
                    target: '_blank',
                  })}
                  alignItems="center"
                >
                  {icon ? (
                    <SunshineCard.Slot name="aside">
                      <SquircleIcon name={icon} />
                    </SunshineCard.Slot>
                  ) : null}
                  <Flex flex={1} flexDirection="column">
                    {description ? (
                      <>
                        <Text fontWeight="weight-500">{sectionItemTitle}</Text>
                        <Text>{description}</Text>
                      </>
                    ) : (
                      <Text fontWeight="weight-500" variant="primary">
                        {sectionItemTitle}
                      </Text>
                    )}
                  </Flex>
                  <SunshineCard.Slot name="action">
                    <SunshineIcon
                      color="grey.600"
                      name="download"
                      size="icon-24"
                    />
                  </SunshineCard.Slot>
                </SunshineCard>
              ),
            )}
          </SunshineCard.Group>
        </section>
      ))}

      {sections.length > 0 && hasMore ? (
        <Box marginY="space-24">
          <Row justifyContent="center">
            <Button
              isLoading={isFetchingMore}
              onClick={handleFetchMore}
              variant="secondary"
            >
              {locales.fetchMore}
            </Button>
          </Row>
        </Box>
      ) : null}
    </>
  );
};

export default SectionList;
